import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { Dropdown } from 'components/common'
import { DateTime } from 'components/common/input/dateTime'
import toast from 'react-hot-toast'
import * as yup from 'yup'
import { handleError } from 'utils/helpers/handleError'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { startOfDayUtc, endOfDayUtc } from 'utils/helpers/date'
import { downloadRma } from 'utils/api/rmaApi'
import { CustomObject } from 'types/common'
import ReportSubmitButton from './reportSubmitButton'

dayjs.extend(isSameOrAfter)

type ErpSyncValue = 'all' | 'true' | 'false'
type DateTypeValue = 'rmaCreatedDate' | 'orderPlacedDate'

type ExportRmaFormValues = {
  dateType: DateTypeValue
  from: string
  to: string
  erpSync: ErpSyncValue
}

const MAX_RANGE_DAYS = 31

const ERP_SYNC_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'ERP Synced', value: 'true' },
  { label: 'Not Synced', value: 'false' },
]

const DATE_TYPE_OPTIONS = [
  { label: 'RMA Creation', value: 'rmaCreatedDate' },
  { label: 'Order Placed', value: 'orderPlacedDate' },
]

// Maps the selected date-type to the API field names the date range should
// be sent under.
const DATE_TYPE_API_FIELDS: Record<
  DateTypeValue,
  { from: string; to: string }
> = {
  rmaCreatedDate: { from: 'from', to: 'to' },
  orderPlacedDate: { from: 'orderPlacedFrom', to: 'orderPlacedTo' },
}

const exportRmaSchema = yup.object({
  dateType: yup
    .string<DateTypeValue>()
    .oneOf(
      ['rmaCreatedDate', 'orderPlacedDate'],
      'dateType must be one of "rmaCreatedDate" or "orderPlacedDate"',
    )
    .required(),
  from: yup
    .string()
    .required('From date is required')
    .test('no-future', 'From date cannot be a future date', (value) => {
      if (!value) return true
      return !dayjs(value).isAfter(dayjs(), 'day')
    }),
  to: yup
    .string()
    .required('To date is required')
    .test('no-future', 'To date cannot be a future date', (value) => {
      if (!value) return true
      return !dayjs(value).isAfter(dayjs(), 'day')
    })
    .test('is-after-from', 'To must be after From', function (value) {
      const { from } = this.parent
      if (!from || !value) return true
      return dayjs(value).isSameOrAfter(dayjs(from))
    })
    .test(
      'max-range',
      `Date range cannot exceed ${MAX_RANGE_DAYS} days`,
      function (value) {
        const { from } = this.parent
        if (!from || !value) return true
        return dayjs(value).diff(dayjs(from), 'day') <= MAX_RANGE_DAYS - 1
      },
    ),
  erpSync: yup
    .string<ErpSyncValue>()
    .oneOf(
      ['all', 'true', 'false'],
      'erpSync must be one of "true", "false" or "all"',
    )
    .required(),
})

const ExportRmaForm = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ExportRmaFormValues>({
    resolver: yupResolver(exportRmaSchema),
    defaultValues: {
      dateType: 'orderPlacedDate',
      from: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
      erpSync: 'all',
    },
  })

  const handleFormSubmit = async (data: ExportRmaFormValues) => {
    const toastId = toast.loading('Loading...', { duration: Infinity })

    try {
      const { from: fromField, to: toField } =
        DATE_TYPE_API_FIELDS[data.dateType]

      const filters: CustomObject = {
        [fromField]: startOfDayUtc(data.from),
        [toField]: endOfDayUtc(data.to),
      }
      // `all` is the API default, so only send the param when it narrows the result
      if (data.erpSync !== 'all') {
        filters.erpSync = data.erpSync
      }

      await downloadRma(filters)

      toast.success('File will be sent to you in email!', {
        id: toastId,
        duration: 3000,
      })
    } catch (error) {
      handleError(error, toastId)
    }
  }

  return (
    <div>
      <h3 className="title">Export RMA</h3>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="mb-5 flex flex-col gap-3"
      >
        <div className="flex gap-3">
          <Dropdown
            label="Date Type"
            name="dateType"
            options={DATE_TYPE_OPTIONS}
            defaultValue="orderPlacedDate"
            setValue={setValue}
            searchable={false}
            error={errors.dateType?.message}
          />

          <Dropdown
            label="ERP Sync"
            name="erpSync"
            options={ERP_SYNC_OPTIONS}
            defaultValue="all"
            setValue={setValue}
            searchable={false}
            error={errors.erpSync?.message}
          />
        </div>

        <div className="flex gap-3">
          <DateTime
            required
            label="From"
            name="from"
            register={register}
            max={dayjs().format('YYYY-MM-DD')}
            error={errors.from?.message}
          />
          <DateTime
            required
            label="To"
            name="to"
            max={dayjs().format('YYYY-MM-DD')}
            register={register}
            error={errors.to?.message}
          />
        </div>

        <ReportSubmitButton buttonText="Export" loading={isSubmitting} />
      </form>
    </div>
  )
}

export default ExportRmaForm
